import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { getInvitationDetails, respondInvitationPublic } from "../../services/user.service";
import Swal from "sweetalert2";
import { handleServerValidations, setLocalKey, getLocalKey } from "../../../../utils/commonfunction";
import { useDispatch } from "react-redux";
import { addProfileData } from "../../../../redux/slices/profileslice";
import { ROLE } from "../../../../utils/Constants";
import { useFormik } from "formik";
import * as Yup from "yup";

export default function AcceptInvite() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(true);
    const [inviteDetails, setInviteDetails] = useState(null);

    const [name, setName] = useState("");
    const [password, setPassword] = useState("");

    const [accepted, setAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const Dispatch = useDispatch();

    useEffect(() => {
        if (!token) {
            navigate("/");
            return;
        }
        fetchDetails();
    }, [token, navigate]);

    const fetchDetails = async () => {
        try {
            const resp = await getInvitationDetails(token);
            if (resp.success) {
                setInviteDetails(resp.data);
                if (resp.data.name) setName(resp.data.name);
            } else {
                Swal.fire({ icon: "error", title: "Oops...", text: resp.message || "Invalid invitation" }).then(() => navigate('/'));
            }
        } catch (err) {
            Swal.fire({ icon: "error", title: "Error", text: "Failed to load invitation" }).then(() => navigate('/'));
        } finally {
            setLoading(false);
        }
    };
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: inviteDetails?.name || "",
            password: ""
        },
        validationSchema: Yup.object().shape({
            name: inviteDetails?.userExists
                ? Yup.string()
                : Yup.string().required("Name is required"),
            password: inviteDetails?.userExists
                ? Yup.string()
                : Yup.string()
                    .required("Password is required")
                    .min(8, "Password must be at least 8 characters")
                    .matches(
                        /^[A-Z].*$/,
                        "Password must start with a capital letter"
                    )
        }),
    });
    const handleAction = async (action) => {
        if (action === "Accept" && !inviteDetails?.userExists) {
            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) {
                formik.setTouched({
                    name: true,
                    password: true
                });
                return;
            }
        }

        setSubmitting(true);
        try {
            const resp = await respondInvitationPublic({
                token,
                action,
                name: inviteDetails?.userExists
                    ? inviteDetails?.name
                    : formik.values.name,
                password: inviteDetails?.userExists
                    ? undefined
                    : formik.values.password
            });

            if (resp.success) {
                if (action === "Accept") {
                    if (resp.data?.isExistingUser) {
                        setAccepted(true);
                        // We mark them as accepted but we don't dispatch profile data since they didn't log in
                    } else {
                        setLocalKey(ROLE.USER, JSON.stringify(resp.data));
                        Dispatch(addProfileData(resp.data));
                        setAccepted(true);
                    }
                } else {
                    Swal.fire({ icon: "success", text: "Invitation rejected." }).then(() => navigate("/"));
                }
            } else {
                Swal.fire({ icon: "error", text: handleServerValidations(resp) });
            }
        } catch (err) {
            Swal.fire({ icon: "error", text: err.message || "Something went wrong" });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="loader"></div>
            </div>
        );
    }
   
    const seller = inviteDetails?.seller;
    const isExistingUser = inviteDetails?.userExists;
    const loggedInUser = getLocalKey(ROLE.USER) ? JSON.parse(getLocalKey(ROLE.USER) || "{}") : null;
    const isAlreadyLoggedIn = Boolean(loggedInUser && loggedInUser._id && loggedInUser.token);

    return (
        <div className="min-h-[70vh] flex flex-col items-center pt-10 pb-20 bg-gray-50">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Invitation for Staff</h2>

            {accepted ? (
                <div className="bg-white p-8 rounded-xl shadow-[0px_4px_24px_rgba(0,0,0,0.05)] w-[90%] max-w-[450px] text-center border border-gray-100">
                    <div className="bg-gray-50 rounded-lg p-6 mb-6 flex flex-col items-center">
                        <div className="h-16 w-16 rounded-full overflow-hidden bg-gray-200 mb-3">
                            <img 
                                src={seller?.profile_image || "/images/User.png"} 
                                onError={(e) => { e.target.onerror = null; e.target.src = '/images/User.png'; }}
                                alt="seller" 
                                className="w-full h-full object-cover" 
                            />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">{seller?.name || "The Store"}</h3>
                        <p className="text-sm text-gray-500 mt-1">{seller?.store_name }</p>
                    </div>

                    <p className="text-gray-600 mb-6">Your invitation has been accepted.</p>

                    <Link
                        to={isExistingUser && !isAlreadyLoggedIn ? "/user/login?redirect=/user/staff-invitations" : "/user/staff-invitations"}
                        className="btn btn-pri w-full block text-center"
                    >
                        {isExistingUser && !isAlreadyLoggedIn ? "Login" : "Go to Staff Portal"}
                    </Link>
                </div>
            ) : (
                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-[0px_4px_24px_rgba(0,0,0,0.05)] w-[90%] max-w-[450px] border border-gray-100">
                        <div className="bg-gray-50 rounded-lg p-6 mb-8 flex flex-col items-center">
                            <div className="h-16 w-16 rounded-full overflow-hidden bg-gray-200 mb-3">
                                <img 
                                    src={seller?.profile_image || "/images/User.png"} 
                                    onError={(e) => { e.target.onerror = null; e.target.src = '/images/User.png'; }}
                                    alt="seller" 
                                    className="w-full h-full object-cover" 
                                />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">{seller?.name|| "The Store"}</h3>
                            <p className="text-sm text-gray-500 mt-1">{seller?.store_name }</p>
                        </div>

                        {isExistingUser && (
                            <div className="mb-6 p-4 bg-orange-50 border border-orange-100 rounded-lg text-sm text-orange-800">
                                An account with this email already exists. You can accept the invitation and login to access the Staff portal.
                            </div>
                        )}

                        {!isExistingUser && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-100 outline-none focus:border-pink-500"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.name && formik.errors.name && (
                                        <p className="text-red-500 text-xs mt-1">{formik.errors.name}</p>
                                    )}
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                            <input
                                type="email"
                                className="w-full px-4 py-3 rounded-lg bg-gray-50 border-none text-gray-500 cursor-not-allowed"
                                value={inviteDetails?.email || ""}
                                readOnly
                                disabled
                            />
                        </div>
                            {!isExistingUser && (
                                <div className="mb-8">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Create Password
                                    </label>

                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            placeholder="Enter password"
                                            className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-100 outline-none focus:border-pink-500"
                                            value={formik.values.password}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        />

                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(prev => !prev)}
                                            className="absolute inset-y-0 right-0 flex items-center pr-3"
                                        >
                                            {showPassword ? (
                                                <img src="/images/eye2.svg" alt="hide" />
                                            ) : (
                                                <img src="/images/eye1.svg" alt="show" />
                                            )}
                                        </button>
                                    </div>

                                    {formik.touched.password && formik.errors.password && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {formik.errors.password}
                                        </p>
                                    )}
                                </div>
                            )}

                        <div className="grid grid-cols-2 gap-4 mt-6">
                            <button
                                disabled={submitting}
                                onClick={() => handleAction("Accept")}
                                className="btn btn-pri w-full !bg-[#FF63B5] !border-[#FF63B5]"
                            >
                                {submitting ? "Processing..." : "Accept"}
                            </button>
                            <button
                                disabled={submitting}
                                onClick={() => handleAction("Reject")}
                                className="w-full py-2 px-4 rounded-lg border border-[#FF63B5] text-[#FF63B5] hover:bg-pink-50 transition font-medium"
                            >
                                Reject
                            </button>
                        </div>
                    </div>
                )}
            </div>
    );
}
